import { SourceFamily, WorkItem, workItemsApi } from '../api/workItems';

/** Workbench query key for the source-family anchor. Same family the drawer already loads. */
export const SOURCE_HOME_QUERY_PARAM = 'sourceHome';

/** Pile name while prev/next walks that document's next steps, not the Open pile. */
export const DOCUMENT_FAMILY_PILE_LABEL = 'This document';

type FamilySnapshot = Pick<SourceFamily, 'discoverable' | 'source_kind' | 'source_home_id' | 'tasks'> | null | undefined;

/**
 * Anchor id for a document family the drawer can already open.
 * The source home when one exists; otherwise the open item, which resolves the same batch.
 */
export function documentFamilyWorkbenchAnchor(itemId: string, family: FamilySnapshot): string | null {
    if (!family?.discoverable || family.source_kind !== 'document') return null;
    if (!Array.isArray(family.tasks) || family.tasks.length < 2) return null;
    const homeId = typeof family.source_home_id === 'string' ? family.source_home_id.trim() : '';
    return homeId || itemId;
}

/** Workbench URL for Open in Workbench. Document families keep the sibling set; everything else stays focus-only. */
export function workbenchPathForDocumentFamily(itemId: string, family: FamilySnapshot): string {
    const params = new URLSearchParams();
    params.set('focusId', itemId);
    const anchor = documentFamilyWorkbenchAnchor(itemId, family);
    if (anchor) params.set(SOURCE_HOME_QUERY_PARAM, anchor);
    return `/app/workbench?${params.toString()}`;
}

export function dropDocumentFamilyScope(params: URLSearchParams): URLSearchParams {
    params.delete(SOURCE_HOME_QUERY_PARAM);
    return params;
}

export type DocumentFamilyLoad =
    | { status: 'siblings'; items: WorkItem[] }
    | { status: 'not-document-family' };

/**
 * The verb tasks the drawer lists under "From this document".
 * Every task must load. A short list would claim a family the drawer did not show.
 */
export async function loadDocumentFamilySiblings(anchorId: string): Promise<DocumentFamilyLoad> {
    const family = await workItemsApi.getSourceFamily(anchorId);
    const anchor = documentFamilyWorkbenchAnchor(anchorId, family);
    if (!anchor) return { status: 'not-document-family' };
    let items: WorkItem[];
    try {
        items = await Promise.all(family.tasks.map((task) => workItemsApi.getWorkItem(task.id)));
    } catch {
        throw new Error('document family siblings unavailable');
    }
    if (items.length !== family.tasks.length || items.some((row) => !row?.id)) {
        throw new Error('document family siblings unavailable');
    }
    return { status: 'siblings', items };
}
